import type { AssistantArtifact } from './message';
import { isRecord } from './parse';

const readSize = (value: unknown) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
};

const toArtifact = (
  item: Record<string, unknown>
): AssistantArtifact | null => {
  const name = typeof item.name === 'string' ? item.name.trim() : '';
  const url = typeof item.url === 'string' ? item.url.trim() : '';
  if (!name && !url) return null;
  const mimeType =
    typeof item.mime_type === 'string' ? item.mime_type.trim() : '';
  const type = typeof item.type === 'string' ? item.type.trim() : '';
  return {
    url,
    name: name || '未命名文件',
    mime_type: mimeType,
    size: readSize(item.size),
    ...(type ? { type } : {}),
  };
};

const fromUnknown = (raw: unknown): AssistantArtifact[] => {
  if (Array.isArray(raw)) {
    return raw.flatMap((item) => {
      if (!isRecord(item)) return [];
      const file = toArtifact(item);
      return file ? [file] : [];
    });
  }
  if (!isRecord(raw)) return [];
  const file = toArtifact(raw);
  return file ? [file] : [];
};

/** 再解一层：有的帧把数组又包成字符串。失败就沿用内层原文。 */
const unwrapJson = (parsed: unknown): unknown => {
  if (typeof parsed !== 'string') return parsed;
  try {
    return JSON.parse(parsed) as unknown;
  } catch {
    return parsed;
  }
};

/**
 * `attachment` 事件的 text：文件数组的 JSON 串。
 * 解析失败返回空列表，不抛，避免整条消息坏掉。
 */
export const artifactsFromText = (text: string): AssistantArtifact[] => {
  const trimmed = text.trim();
  if (!trimmed) return [];
  try {
    return fromUnknown(unwrapJson(JSON.parse(trimmed) as unknown));
  } catch {
    return [];
  }
};

const sameFile = (left: AssistantArtifact, right: AssistantArtifact) =>
  left.url === right.url && left.name === right.name;

/** 后来的相同 url+name 不重复追加。没有新增时返回原数组。 */
export const mergeArtifacts = (
  current: AssistantArtifact[],
  incoming: AssistantArtifact[]
): AssistantArtifact[] => {
  if (!incoming.length) return current;
  const next = current.slice();
  incoming.forEach((file) => {
    if (next.some((item) => sameFile(item, file))) return;
    next.push(file);
  });
  return next.length === current.length ? current : next;
};

/** 按块表里的 attachment 文本收集。其它 type 忽略。 */
export const artifactsFromBlocks = (
  blocks: Array<{ type: string; text: string }>
): AssistantArtifact[] => {
  let files: AssistantArtifact[] = [];
  blocks.forEach((block) => {
    if (block.type !== 'attachment') return;
    files = mergeArtifacts(files, artifactsFromText(block.text));
  });
  return files;
};
